import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Plus, Minus } from 'lucide-react';
import { RarityPicker } from './RarityPicker';
import type { RarityTag } from '../lib/rarity';

type Rarity = 'common' | 'shiny' | 'legend';

type Props = {
  number: number;
  code?: string | null;           // ej "IRQ-1" → se muestra como "IRQ 1"
  rarity?: Rarity;
  state?: 'have' | 'missing' | 'duplicate';
  duplicates?: number;            // cuantía si state==='duplicate'
  imageUrl?: string | null;
  playerName?: string | null;
  team?: string | null;
  onClick?: () => void;
  onIncrement?: () => void;
  onDecrement?: () => void;
  size?: 'sm' | 'md' | 'lg';
  /** Tag de rareza puesto por el usuario (borde parallel WC2026). */
  rarityTag?: RarityTag | null;
  onRarityChange?: (tag: RarityTag | null) => void;
};

/**
 * Tarjeta de laminita con:
 *  • tilt 3D al mover el cursor (sigue al puntero)
 *  • foil holográfico animado para shiny/legend
 *  • estado visual: tenida / faltante (slot vacío) / repetida (badge)
 *  • tappable, con feedback de scale
 */
export function HoloStickerCard({
  number,
  code,
  rarity = 'common',
  state = 'have',
  duplicates = 0,
  imageUrl,
  playerName,
  team,
  onClick,
  onIncrement,
  onDecrement,
  size = 'md',
  rarityTag,
  onRarityChange,
}: Props) {
  const display = code ? code.replace(/-/g, ' ') : String(number);
  const ref = useRef<HTMLButtonElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, mx: 50, my: 50 });

  const sizeClass =
    size === 'sm' ? 'w-20' : size === 'lg' ? 'w-44' : 'w-28';

  function onMove(e: React.MouseEvent) {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * 100;
    const py = ((e.clientY - r.top) / r.height) * 100;
    setTilt({
      x: (py - 50) / 8,
      y: -(px - 50) / 8,
      mx: px, my: py,
    });
  }
  function onLeave() {
    setTilt({ x: 0, y: 0, mx: 50, my: 50 });
  }

  if (state === 'missing') {
    return (
      <div className={clsx(sizeClass, 'slot-empty group')}>
        <button
          onClick={onClick}
          className="absolute inset-0 flex flex-col items-center justify-center"
          aria-label="marcar como tengo"
        >
          <span className="font-mono text-sm font-bold leading-tight text-center">
            {display}
          </span>
          {playerName && (
            <span className="mt-0.5 px-1 text-2xs text-haze line-clamp-1 text-center">
              {playerName}
            </span>
          )}
          <span className="absolute bottom-1 left-1 right-1 text-center text-2xs uppercase tracking-wide font-bold text-[#FF6B7A]">
            falta
          </span>
        </button>
        {onIncrement && (
          <button
            onClick={(e) => { e.stopPropagation(); onIncrement(); }}
            aria-label="añadir"
            className="absolute top-1 right-1 z-10 grid h-7 w-7 place-items-center rounded-full bg-flame text-white shadow-glow-flame hover:scale-110 transition"
          >
            <Plus size={14} strokeWidth={3} />
          </button>
        )}
      </div>
    );
  }

  const foil = rarity === 'legend' || rarity === 'shiny';

  return (
    <motion.button
      ref={ref}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileTap={{ scale: 0.95 }}
      style={{
        transform: `perspective(700px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        ['--mx' as any]: `${tilt.mx}%`,
        ['--my' as any]: `${tilt.my}%`,
      }}
      className={clsx(
        sizeClass,
        'slot-filled group relative',
        rarity === 'legend' && 'shadow-glow-gold',
        rarity === 'shiny' && 'shadow-glow-cyan',
      )}
    >
      {/* Wrapper interno que sí clipea (imagen + foil + gradiente). */}
      <div className={clsx('slot-filled-clip', foil && 'holo-foil')}>
        {/* imagen / placeholder */}
        <div className="absolute inset-0 grid place-items-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={playerName ?? `Sticker ${number}`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="text-center">
              <div className="font-display font-extrabold text-2xl text-foam/90 leading-tight">
                {display}
              </div>
              {playerName && (
                <div className="mt-1 px-2 text-2xs text-haze line-clamp-2 font-medium">
                  {playerName}
                </div>
              )}
            </div>
          )}
        </div>

        {/* gradient base inferior con info — dentro del clip para no salir del rounded */}
        {(playerName || team) && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-1.5">
            {team && (
              <div className="text-2xs uppercase tracking-wider text-haze/90 line-clamp-1">
                {team}
              </div>
            )}
          </div>
        )}
      </div>

      {/* código (fuera del clip → no se corta) */}
      <span className="num-badge">{display}</span>

      {/* rarity icon */}
      {rarity !== 'common' && (
        <span
          className={clsx(
            'absolute top-1 left-1 z-10 rounded-md px-1 py-0.5 text-2xs font-bold uppercase tracking-wider',
            rarity === 'legend' ? 'bg-gold/90 text-ink' : 'bg-cyan/90 text-ink',
          )}
        >
          {rarity === 'legend' ? '★' : '◆'}
        </span>
      )}

      {/* duplicate badge */}
      {state === 'duplicate' && duplicates > 0 && (
        <div className="absolute -top-2 -right-2 z-20 grid h-7 w-7 place-items-center rounded-full bg-cyan shadow-glow-cyan text-ink text-xs font-bold">
          ×{duplicates + 1}
        </div>
      )}

      {/* rarity tag picker (borde parallel WC2026) */}
      {onRarityChange && (
        <div className="absolute bottom-1 left-1 z-20">
          <RarityPicker value={rarityTag ?? null} onChange={onRarityChange} />
        </div>
      )}

      {/* +/- floating controls (manual repes) */}
      {(onIncrement || onDecrement) && (
        <div className="absolute bottom-1 right-1 z-20 flex gap-1">
          {onDecrement && (
            <button
              onClick={(e) => { e.stopPropagation(); onDecrement(); }}
              aria-label="quitar"
              className="grid h-7 w-7 place-items-center rounded-full bg-void/85 border border-line text-haze hover:text-foam hover:bg-void transition"
            >
              <Minus size={12} strokeWidth={3} />
            </button>
          )}
          {onIncrement && (
            <button
              onClick={(e) => { e.stopPropagation(); onIncrement(); }}
              aria-label="añadir repe"
              className="grid h-7 w-7 place-items-center rounded-full bg-lime text-ink shadow-glow-lime hover:scale-110 transition"
            >
              <Plus size={12} strokeWidth={3} />
            </button>
          )}
        </div>
      )}

    </motion.button>
  );
}
